/**
 * 设置 · Tabbed settings shell.
 *
 * Tab 1「一般设置」(`#general`) — 跟账号走的偏好（B1 prefs）：
 *   外观（主题、侧边栏折叠默认）/ 镜像清晰度 / 终端 / 网络偏好。
 *   Backend: GET / PUT /api/me/prefs
 *
 * Tab 2「手机设备设置」(`#device`) — 这台电脑上的 adh 进程 + agent-local
 *   配置（C 类）。Backend: /api/admin/* + /api/agent/config
 *   实现在 [LocalService.tsx](LocalService.tsx) 的 `DeviceSettingsTab`。
 *
 * The active tab is driven by `location.hash`; switching tabs writes the
 * hash and preserves browser back/forward semantics. Default tab on
 * empty hash is `#general`. The legacy `/local-service` route redirects
 * to `/settings#device` so old bookmarks keep working.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Palette,
  Server,
  Settings as SettingsIcon,
  SlidersHorizontal,
  Terminal,
  Wifi,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  useGlobalPrefs,
  useGlobalPrefsUpdate,
  useMyPrefsStatus,
} from '@/lib/myPrefs';
import { DeviceSettingsTab } from './LocalService';

type TabId = 'general' | 'device';

interface TabDef {
  id: TabId;
  label: string;
  icon: JSX.Element;
}

const TABS: TabDef[] = [
  { id: 'general', label: '一般设置', icon: <SlidersHorizontal className="h-4 w-4" /> },
  { id: 'device', label: '手机设备设置', icon: <Server className="h-4 w-4" /> },
];

function parseTabFromHash(hash: string): TabId {
  const raw = hash.replace(/^#/, '');
  return raw === 'device' ? 'device' : 'general';
}

export default function Settings(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState<TabId>(() => parseTabFromHash(location.hash));

  useEffect(() => {
    setActive(parseTabFromHash(location.hash));
  }, [location.hash]);

  const onSwitch = (id: TabId): void => {
    if (id === active) return;
    navigate({ pathname: '/settings', hash: id === 'general' ? '' : `#${id}` }, { replace: false });
  };

  return (
    <div className="space-y-4 p-4">
      <header>
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <SettingsIcon className="h-5 w-5 text-accent" /> 设置
        </h1>
        <p className="mt-1 text-xs text-ink-muted">
          一般设置跟随账号同步；手机设备设置只对当前 hub 主机生效。
        </p>
      </header>

      <nav
        aria-label="设置分组"
        className="flex flex-wrap items-center gap-1 border-b border-line"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSwitch(t.id)}
            className={clsx(
              'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition',
              active === t.id
                ? 'border-accent text-accent'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </nav>

      {active === 'general' ? <GeneralSettingsTab /> : <DeviceSettingsTab />}
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Tab 1 · 一般设置 (B1 prefs)                                       */
/* ----------------------------------------------------------------- */

function GeneralSettingsTab(): JSX.Element {
  return (
    <div className="space-y-4">
      <SaveStatusBar />
      <AppearanceCard />
      <MirrorPrefsCard />
      <TerminalPrefsCard />
      <NetworkPrefsCard />
    </div>
  );
}

function SaveStatusBar(): JSX.Element {
  const { saving, lastError } = useMyPrefsStatus();
  return (
    <div className="flex items-center gap-3 text-[11px]">
      <span
        className={clsx(
          'inline-flex items-center gap-1',
          saving ? 'text-amber-600' : 'text-emerald-600',
        )}
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
        {saving ? '保存中…' : '已同步'}
      </span>
      {lastError && (
        <span className="inline-flex items-center gap-1 text-rose-500">
          <AlertTriangle className="h-3 w-3" /> {lastError}
        </span>
      )}
    </div>
  );
}

function Card({
  title,
  icon,
  subtitle,
  children,
}: {
  title: string;
  icon?: JSX.Element;
  subtitle?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {title}
        </h2>
        {subtitle && (
          <span className="hidden text-[10px] uppercase tracking-wide text-ink-dim md:inline">
            {subtitle}
          </span>
        )}
      </header>
      <div className="space-y-3 px-3 py-3 text-sm">{children}</div>
    </section>
  );
}

function AppearanceCard(): JSX.Element {
  const theme = useGlobalPrefs((g) => g.ui.theme);
  const sidebarCollapsed = useGlobalPrefs((g) => g.ui.sidebar.collapsed);
  const update = useGlobalPrefsUpdate();
  return (
    <Card title="外观" icon={<Palette className="h-4 w-4 text-accent" />}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-ink-muted">主题</span>
        <div className="inline-flex rounded-md border border-line bg-bg-card p-0.5 text-xs">
          {(['auto', 'light', 'dark'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              className={clsx(
                'rounded px-2 py-1 transition',
                theme === opt
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-muted hover:text-ink',
              )}
              onClick={() => update({ ui: { theme: opt } })}
            >
              {opt === 'auto' ? '跟随系统' : opt === 'light' ? '浅色' : '深色'}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-ink-muted">
        <input
          type="checkbox"
          checked={sidebarCollapsed}
          onChange={(e) => update({ ui: { sidebar: { collapsed: e.target.checked } } })}
        />
        默认折叠侧边栏（窄屏可省一截宽度）
      </label>
    </Card>
  );
}

function MirrorPrefsCard(): JSX.Element {
  const mirror = useGlobalPrefs((g) => g.mirror);
  const update = useGlobalPrefsUpdate();
  const sizeOptions: Array<{ v: number | null; label: string; hint?: string }> = [
    { v: null, label: 'Server 默认', hint: 'USB 1080/60，无线 900/30' },
    { v: -1, label: '自动' },
    { v: 0, label: '原生分辨率' },
    { v: 540, label: '540p' },
    { v: 720, label: '720p' },
    { v: 1080, label: '1080p' },
  ];
  const bitrateOptions: Array<{ v: number | null; label: string }> = [
    { v: null, label: 'Server 默认' },
    { v: 512_000, label: '512k' },
    { v: 1_024_000, label: '1M' },
    { v: 2_048_000, label: '2M' },
    { v: 4_096_000, label: '4M' },
    { v: 8_192_000, label: '8M' },
    { v: 16_384_000, label: '16M' },
  ];
  return (
    <Card title="镜像清晰度" subtitle="账号偏好">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Selector
          label="最大尺寸"
          value={mirror.maxSize}
          options={sizeOptions}
          onChange={(v) => update({ mirror: { maxSize: v } })}
        />
        <Selector
          label="最大 FPS"
          value={mirror.maxFps}
          options={[
            { v: null, label: 'Server 默认' },
            { v: -1, label: '自动' },
            { v: 30, label: '30' },
            { v: 60, label: '60' },
            { v: 90, label: '90' },
            { v: 120, label: '120' },
          ]}
          onChange={(v) => update({ mirror: { maxFps: v } })}
        />
        <Selector
          label="比特率"
          value={mirror.bitRate}
          options={bitrateOptions}
          onChange={(v) => update({ mirror: { bitRate: v } })}
        />
      </div>
      <p className="text-[11px] text-ink-muted">
        这里改的是默认偏好；浮动镜像窗口里的工具栏滑块仍可临时调整本次会话。
      </p>
    </Card>
  );
}

function Selector<T extends number | null>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ v: T; label: string; hint?: string }>;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-ink-muted">{label}</span>
      <select
        className="select"
        value={String(value ?? 'null')}
        onChange={(e) => {
          const raw = e.target.value;
          const opt = options.find((o) => String(o.v ?? 'null') === raw);
          if (opt) onChange(opt.v);
        }}
      >
        {options.map((o) => (
          <option key={String(o.v ?? 'null')} value={String(o.v ?? 'null')}>
            {o.label}
            {o.hint ? ` · ${o.hint}` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function TerminalPrefsCard(): JSX.Element {
  const terminal = useGlobalPrefs((g) => g.terminal);
  const update = useGlobalPrefsUpdate();
  return (
    <Card title="终端" icon={<Terminal className="h-4 w-4 text-accent" />}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">字体（CSS font-family，留空使用默认）</span>
          <input
            className="input"
            value={terminal.font}
            placeholder="JetBrains Mono, Menlo, monospace"
            onChange={(e) => update({ terminal: { font: e.target.value } })}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">字号 (px)</span>
          <input
            className="input"
            type="number"
            min={9}
            max={32}
            value={terminal.fontSize}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isFinite(n) && n >= 9 && n <= 32) update({ terminal: { fontSize: n } });
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">默认 shell（留空让 server 选）</span>
          <input
            className="input"
            value={terminal.shell}
            placeholder="/bin/zsh"
            onChange={(e) => update({ terminal: { shell: e.target.value } })}
          />
        </label>
      </div>
    </Card>
  );
}

function NetworkPrefsCard(): JSX.Element {
  const transport = useGlobalPrefs((g) => g.transport.preferred);
  const autoswitch = useGlobalPrefs((g) => g.autoswitch.enabled);
  const proxyMru = useGlobalPrefs((g) => g.proxy.mru);
  const update = useGlobalPrefsUpdate();
  return (
    <Card title="网络偏好" icon={<Wifi className="h-4 w-4 text-accent" />}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-ink-muted">默认通道</span>
        <div className="inline-flex rounded-md border border-line bg-bg-card p-0.5 text-xs">
          {(['auto', 'usb', 'wifi'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              className={clsx(
                'rounded px-2 py-1 transition',
                transport === opt
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-muted hover:text-ink',
              )}
              onClick={() => update({ transport: { preferred: opt } })}
            >
              {opt === 'auto' ? '自动' : opt === 'usb' ? 'USB' : 'Wi-Fi'}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-ink-muted">
        <input
          type="checkbox"
          checked={autoswitch}
          onChange={(e) => update({ autoswitch: { enabled: e.target.checked } })}
        />
        自动在 USB / Wi-Fi 之间切换（基于实时探测）
      </label>
      <div>
        <p className="mb-1 text-[11px] text-ink-muted">代理历史 (MRU)</p>
        {proxyMru.length === 0 ? (
          <p className="text-[11px] text-ink-dim">暂无；在「网络」面板手动配过的代理会出现在这里。</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {proxyMru.map((s) => (
              <li
                key={s}
                className="inline-flex items-center gap-1.5 rounded border border-line bg-bg-soft px-2 py-1 text-[11px] font-mono"
              >
                {s}
                <button
                  className="text-ink-dim hover:text-rose-500"
                  title="从历史中移除"
                  onClick={() =>
                    update({ proxy: { mru: proxyMru.filter((x) => x !== s) } })
                  }
                >
                  <XCircle className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
